<template>
  <section class="ncrm-external-form-privacy d-flex flex-column ga-3">
    <div class="d-flex align-center justify-space-between flex-wrap ga-2">
      <div>
        <div class="text-body-2 font-weight-semibold ncrm-text-strong">{{ $t(field ? `${copyKey}.fieldTitle.${field}` : `${copyKey}.title`) }}</div>
        <div class="text-caption text-medium-emphasis mt-1">{{ $t(field ? `${copyKey}.fieldHint` : `${copyKey}.hint`) }}</div>
      </div>
      <v-chip :color="statusColor">{{ $t(`${copyKey}.status.${localStatus}`) }}</v-chip>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" height="2" />
    <v-alert v-else-if="loadError" type="error" density="compact">
      {{ $t(`${copyKey}.${loadError === 403 ? "forbidden" : "loadError"}`) }}
      <template v-if="loadError !== 403" #append>
        <v-btn size="small" variant="text" @click="loadOptions">{{ $t(`${copyKey}.retry`) }}</v-btn>
      </template>
    </v-alert>
    <v-alert v-else-if="!options.purposes.length" type="info" density="compact">
      {{ $t(`${copyKey}.emptyPurposes`) }}
      <a class="ncrm-external-form-privacy__link" v-bind="externalLink(purposesLink)">
        {{ $t(`${copyKey}.openPurposes`) }}<v-icon size="12" class="ms-1">mdi-open-in-new</v-icon>
      </a>
    </v-alert>

    <template v-else>
      <v-alert v-if="form.status === 'active' && localStatus !== 'configured'" type="warning" density="compact">
        {{ $t(`${copyKey}.activeRequiresConfig`) }}
      </v-alert>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-select
            :model-value="privacy.purpose_id"
            :items="purposeItems"
            item-title="title"
            item-value="value"
            :label="$t(`${copyKey}.purpose`)"
            :error-messages="purposeUnavailable ? $t(`${copyKey}.issues.privacy_form_purpose_unavailable`) : []"
            hide-details="auto"
            clearable
            :menu-props="selectMenuProps"
            @update:model-value="selectPurpose"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="privacy.channel_code"
            :items="channelItems"
            :no-data-text="$t(`${copyKey}.noChannels`)"
            item-title="title"
            item-value="value"
            :item-props="channelItemProps"
            :label="$t(`${copyKey}.channel`)"
            :hint="channelHint"
            :error-messages="channelError"
            persistent-hint
            :disabled="!purpose"
            hide-details="auto"
            clearable
            :menu-props="selectMenuProps"
          />
        </v-col>
      </v-row>

      <div v-if="purpose" class="d-flex align-center flex-wrap ga-2 text-body-2">
        <v-icon size="16">{{ purpose.double_opt_in_enabled ? "mdi-email-check-outline" : "mdi-check-circle-outline" }}</v-icon>
        <span>{{ $t(`${copyKey}.${purpose.double_opt_in_enabled ? "doiOn" : "doiOff"}`) }}</span>
        <a class="ncrm-external-form-privacy__link" v-bind="externalLink(purposeLink)">
          {{ $t(`${copyKey}.editPurpose`) }}<v-icon size="12" class="ms-1">mdi-open-in-new</v-icon>
        </a>
      </div>

      <div class="text-caption text-medium-emphasis">{{ $t(`${copyKey}.noticesHint`) }}</div>
      <v-alert v-if="localStatus === 'configured'" type="info" density="compact">
        {{ $t(`${copyKey}.consentFromNotice`) }}
      </v-alert>
      <v-row dense>
        <v-col v-for="locale in requiredLocales" :key="locale" cols="12" sm="6" :md="requiredLocales.length > 3 ? 3 : 4">
          <v-select
            v-model="privacy.notice_version_ids[locale]"
            :items="noticeItems(locale)"
            item-title="title"
            item-value="value"
            :label="$t(`${copyKey}.noticeFor`, { locale: locale.toUpperCase() })"
            :no-data-text="$t(`${copyKey}.noPublishedNotice`)"
            :error-messages="noticeUnavailable(locale) ? $t(`${copyKey}.issues.privacy_form_notice_unavailable`) : []"
            :disabled="!purpose"
            hide-details="auto"
            clearable
            :menu-props="selectMenuProps"
          />
          <a v-if="purpose" class="ncrm-external-form-privacy__link text-caption" v-bind="externalLink(noticeLink(locale))">
            {{ $t(`${copyKey}.${noticeItems(locale).length ? "manageNotice" : "publishNotice"}`) }}<v-icon size="12" class="ms-1">mdi-open-in-new</v-icon>
          </a>
        </v-col>
      </v-row>
    </template>
  </section>
</template>

<script>
import {
  PRIVACY_STATUS_COLORS,
  doiBlocksChannel,
  eligibleChannels,
  fetchExternalFormPrivacyOptions,
  noticesFor,
  privacyDraft,
  privacyNoticesRoute,
  privacyPayload,
  rendererRequirements,
} from "@/Utile/ncrmExternalFormPrivacy.mjs";
import { purposesRoute } from "@/components/ncrm/privacy/privacyPurposeModel.mjs";

// S5.1 — maparea Privacy a formularului: scop activ, canal eligibil pentru
// renderer și scop, notice publicat pentru fiecare limbă servită. DOI rămâne
// proprietatea scopului: aici este numai afișat, cu link spre configurarea lui.
// S5.3B: cu `field`, editează maparea unui câmp de contact al formularului „Colectare
// date” (`form.privacyFields[field]`), cu canalele livrabile pe acel câmp.
export default {
  name: "NcrmWorkflowExternalFormPrivacy",
  props: {
    form: { type: Object, required: true },
    selectMenuProps: { type: Object, default: () => ({}) },
    field: { type: String, default: null },
  },
  emits: ["catalog-refreshed"],
  data: () => ({
    options: { purposes: [], notices: [], renderers: {} },
    loading: false,
    loadError: null,
  }),
  computed: {
    copyKey() { return "ncrm.workflows.nodeSettings.externalForms.privacy"; },
    privacy() {
      return (this.field ? this.form.privacyFields?.[this.field] : this.form.privacy) || privacyDraft(null);
    },
    purpose() { return this.options.purposes.find((item) => item.id === this.privacy.purpose_id) || null; },
    purposeUnavailable() { return Boolean(this.privacy.purpose_id && !this.purpose && !this.loading && !this.loadError); },
    requirements() { return rendererRequirements(this.options, this.form.rendererKey); },
    requiredLocales() { return this.requirements.locales || []; },
    purposeItems() { return this.options.purposes.map((item) => ({ value: item.id, title: item.name })); },
    // Pe un scop cu DOI, canalele telefonice rămân în listă, dezactivate, cu motivul afișat.
    channelItems() {
      return eligibleChannels(this.options, this.form.rendererKey, this.privacy.purpose_id, this.field)
        .map((channel) => ({
          value: channel,
          title: this.$t(`ncrm.contacts.dataProtection.record.channels.${channel}`),
          blocked: doiBlocksChannel(this.purpose, channel),
        }));
    },
    selectableChannels() { return this.channelItems.filter((item) => !item.blocked); },
    // Un canal rămas din alt tip de formular sau scop se semnalează, nu se ascunde.
    channelUnavailable() {
      return Boolean(this.purpose && this.privacy.channel_code &&
        !this.channelItems.some((item) => item.value === this.privacy.channel_code));
    },
    // Un canal telefonic deja salvat pe un scop cu DOI: mesajul serverului, înainte de salvare.
    channelDoiBlocked() { return doiBlocksChannel(this.purpose, this.privacy.channel_code); },
    channelError() {
      if (this.channelUnavailable) return this.$t(`${this.copyKey}.issues.privacy_form_channel_incompatible`);
      return this.channelDoiBlocked ? this.$t(`${this.copyKey}.issues.privacy_form_doi_phone_unsupported`) : [];
    },
    channelHint() {
      if (this.purpose && !this.channelItems.length) return this.$t(`${this.copyKey}.noChannels`);
      return this.channelItems.some((item) => item.blocked) ? this.$t(`${this.copyKey}.doiEmailOnly`) : "";
    },
    // Starea din editor oglindește validarea serverului, ca badge-ul să reacționeze
    // înainte de salvare; serverul rămâne arbitrul la salvare.
    localStatus() {
      // Până sosesc opțiunile, badge-ul arată starea salvată, calculată de server.
      if (this.loading || this.loadError) return this.form.privacy_status || "unconfigured";
      if (!privacyPayload(this.privacy)) return "unconfigured";
      const complete = this.purpose && this.selectableChannels.some((item) => item.value === this.privacy.channel_code) &&
        this.requiredLocales.every((locale) => this.noticeItems(locale).some((item) => item.value === this.privacy.notice_version_ids[locale]));
      return complete ? "configured" : "incomplete";
    },
    statusColor() { return PRIVACY_STATUS_COLORS[this.localStatus]; },
    purposesLink() { return purposesRoute(); },
    purposeLink() { return purposesRoute({ purposeId: this.privacy.purpose_id }); },
  },
  watch: {
    // Formularul (nou, selectat sau widget vechi fără `privacy`) primește un draft
    // complet, ca selecturile să scrie direct în el — ca editoarele surori.
    form: {
      immediate: true,
      handler(form) {
        if (this.field) {
          form.privacyFields = form.privacyFields || {};
          form.privacyFields[this.field] = privacyDraft(form.privacyFields[this.field]);
        } else {
          form.privacy = privacyDraft(form.privacy);
        }
        this.followRepublished();
      },
    },
  },
  created() { this.loadOptions(); },
  mounted() { document.addEventListener("visibilitychange", this.refreshOnReturn); },
  beforeUnmount() { document.removeEventListener("visibilitychange", this.refreshOnReturn); },
  methods: {
    // `silent`: reîncărcare la revenirea în tab, fără să ascundă editorul și fără
    // să înlocuiască o listă bună cu o eroare trecătoare.
    async loadOptions({ silent = false } = {}) {
      let loaded = false;
      if (!silent) {
        this.loading = true;
        this.loadError = null;
      }
      try {
        const { data } = await fetchExternalFormPrivacyOptions();
        this.options = { purposes: data?.purposes || [], notices: data?.notices || [], renderers: data?.renderers || {} };
        this.loadError = null;
        loaded = true;
        this.followRepublished();
      } catch (error) {
        if (!silent) this.loadError = error?.response?.status === 403 ? 403 : "error";
      } finally {
        this.loading = false;
      }
      // Catalogul s-a putut schimba în alt tab: părintele reîmprospătează badge-urile salvate.
      if (silent && loaded) this.$emit("catalog-refreshed");
    },
    // Scopurile și notice-urile se configurează în alt tab; la întoarcere, un
    // notice publicat între timp apare fără redeschiderea drawer-ului.
    refreshOnReturn() {
      if (document.visibilityState === "visible" && !this.loading && this.loadError !== 403) this.loadOptions({ silent: true });
    },
    // Linkurile spre configurarea canonică se deschid în tab nou: editorul are
    // modificări nesalvate, pe care navigarea în același tab le-ar pierde.
    externalLink(route) {
      return { href: this.$router.resolve(route).href, target: "_blank", rel: "noopener noreferrer" };
    },
    noticeItems(locale) {
      return noticesFor(this.options, this.privacy.purpose_id, locale)
        .map((notice) => ({ value: notice.id, title: this.$t(`${this.copyKey}.noticeVersion`, { version: notice.version }) }));
    },
    noticeUnavailable(locale) {
      const id = this.privacy.notice_version_ids[locale];
      return Boolean(id && this.purpose && !this.noticeItems(locale).some((item) => item.value === id));
    },
    // GDPR-077: o versiune mapată, retrasă la republicare, e înlocuită în draft cu
    // versiunea publicată curentă a aceluiași scop și limbi (pe care o servește
    // deja formularul), ca selectorul să arate versiunea reală, nu un ID orfan.
    followRepublished() {
      for (const locale of Object.keys(this.privacy.notice_version_ids)) {
        const id = this.privacy.notice_version_ids[locale];
        const items = this.noticeItems(locale);
        if (id && items.length === 1 && items[0].value !== id) this.privacy.notice_version_ids[locale] = items[0].value;
      }
    },
    noticeLink(locale) { return privacyNoticesRoute({ purposeId: this.privacy.purpose_id, locale }); },
    channelItemProps(item) {
      return item.blocked ? { disabled: true, subtitle: this.$t(`${this.copyKey}.doiEmailOnlyItem`) } : {};
    },
    // Schimbarea scopului invalidează canalul și notice-urile celuilalt scop.
    selectPurpose(purposeId) {
      this.privacy.purpose_id = purposeId || null;
      if (!this.selectableChannels.some((item) => item.value === this.privacy.channel_code)) this.privacy.channel_code = null;
      for (const locale of Object.keys(this.privacy.notice_version_ids)) this.privacy.notice_version_ids[locale] = null;
    },
  },
};
</script>

<style scoped>
.ncrm-external-form-privacy__link { color: rgb(var(--v-theme-primary)); text-decoration: none; }
.ncrm-external-form-privacy__link:hover { text-decoration: underline; }
</style>
